import React, { useEffect, useMemo, useState } from "react";

import {
    Box,
    Typography,
    Paper,
    Tabs,
    Tab,
    Avatar,
    Stack,
    alpha,
    Divider,
    Chip,
    Fade,
} from "@mui/material";

import {
    Building2,
    ImageIcon,
    Mail,
    ShieldCheck,
    Palette,
    UploadCloud,
    Settings2,
} from "lucide-react";

import CustomHeader from "../../components/common/CustomHeader";
import FileUploadModal from "../FileUploadModal";
import CustomButton from "../../components/common/CustomButton";

import { useBranding } from "../../hooks/useBranding";

import { clientApi } from "../../api/tenantApi";

// =========================================================
// CONFIG SECTIONS
// =========================================================

const CONFIG_TABS = [
    {
        key: "branding",
        label: "Branding",
        icon: <Palette size={16} />,
        description: "Manage logos, themes and visual identity",
    },
    // {
    //     key: "email",
    //     label: "Email",
    //     icon: <Mail size={16} />,
    //     description: "SMTP and email templates",
    //     disabled: true,
    // },
    // {
    //     key: "security",
    //     label: "Security",
    //     icon: <ShieldCheck size={16} />,
    //     description: "SSO, domains and access",
    //     disabled: true,
    // },
];

// =========================================================
// REUSABLE SECTION CARD
// =========================================================

const SectionCard = ({
    icon,
    title,
    subtitle,
    children,
}) => {
    return (
        <Paper
            elevation={0}
            sx={{
                borderRadius: "28px",
                p: { xs: 3, md: 4 },
                border: "1px solid #e2e8f0",
                background:
                    "linear-gradient(to bottom, #ffffff, #fbfdff)",
            }}
        >
            <Stack
                direction="row"
                spacing={2}
                alignItems="flex-start"
                mb={4}
            >
                <Avatar
                    sx={{
                        width: 58,
                        height: 58,
                        bgcolor: alpha("#0f766e", 0.08),
                        color: "#0f766e",
                    }}
                >
                    {icon}
                </Avatar>

                <Box>
                    <Typography
                        sx={{
                            fontWeight: 800,
                            fontSize: "1.45rem",
                            color: "#0f172a",
                            mb: 0.6,
                        }}
                    >
                        {title}
                    </Typography>

                    <Typography
                        sx={{
                            color: "#64748b",
                            fontSize: "0.96rem",
                            maxWidth: 700,
                            lineHeight: 1.7,
                        }}
                    >
                        {subtitle}
                    </Typography>
                </Box>
            </Stack>

            {children}
        </Paper>
    );
};

// =========================================================
// LOGO PREVIEW CARD
// =========================================================

const LogoPreviewCard = ({
    logoUrl,
    logoPreview,
    onUploadClick,
    uploading,
}) => {
    return (
        <Paper
            elevation={0}
            sx={{
                borderRadius: "26px",
                overflow: "hidden",
                border: "1px solid #e2e8f0",
                background:
                    "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
            }}
        >
            {/* HEADER */}
            <Box
                sx={{
                    p: 3,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
            >
                <Box>
                    <Typography fontWeight={800}>
                        Organization Logo
                    </Typography>

                    <Typography
                        sx={{ color: "#64748b" }}
                    >
                        Used across platform
                    </Typography>
                </Box>

                <CustomButton
                    startIcon={<UploadCloud size={16} />}
                    onClick={onUploadClick}
                    disabled={uploading}
                >
                    {uploading
                        ? "Uploading..."
                        : "Upload Logo"}
                </CustomButton>
            </Box>

            {/* BODY */}
            <Box
                sx={{
                    minHeight: 340,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    bgcolor: "#f8fafc",
                    position: "relative",
                }}
            >
                {/* BACKGROUND GRID */}
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        opacity: 0.3,
                        backgroundImage: `
              linear-gradient(#e2e8f0 1px, transparent 1px),
              linear-gradient(90deg, #e2e8f0 1px, transparent 1px)
            `,
                        backgroundSize: "24px 24px",
                    }}
                />

                {/* LOGO */}
                {logoPreview || logoUrl ? (
                    <Box
                        component="img"
                        src={
                            logoPreview ||
                            logoUrl
                        }
                        alt="Company Logo"
                        sx={{
                            maxHeight: 180,
                            maxWidth: 300,
                            objectFit: "contain",
                            zIndex: 1,
                        }}
                    />
                ) : (
                    <Stack
                        spacing={2}
                        alignItems="center"
                        sx={{ zIndex: 1 }}
                    >
                        <Avatar>
                            <ImageIcon />
                        </Avatar>

                        <Typography fontWeight={700}>
                            No Logo Uploaded
                        </Typography>
                    </Stack>
                )}
            </Box>
        </Paper>
    );
};

// =========================================================
// FUTURE MODULE PLACEHOLDER
// =========================================================

const FutureModuleCard = ({
    title,
    description,
    icon,
}) => {
    return (
        <Paper
            elevation={0}
            sx={{
                p: 3,
                borderRadius: "22px",
                border: "1px solid #e2e8f0",
                bgcolor: "#ffffff",
                height: "100%",
            }}
        >
            <Stack spacing={2}>
                <Avatar
                    sx={{
                        width: 52,
                        height: 52,
                        bgcolor: alpha("#0f766e", 0.08),
                        color: "#0f766e",
                    }}
                >
                    {icon}
                </Avatar>

                <Box>
                    <Typography
                        sx={{
                            fontWeight: 700,
                            color: "#0f172a",
                            mb: 1,
                        }}
                    >
                        {title}
                    </Typography>

                    <Typography
                        sx={{
                            color: "#64748b",
                            fontSize: "0.92rem",
                            lineHeight: 1.7,
                        }}
                    >
                        {description}
                    </Typography>
                </Box>

                <Chip
                    label="Coming Soon"
                    sx={{
                        width: "fit-content",
                        fontWeight: 700,
                    }}
                />
            </Stack>
        </Paper>
    );
};

export default function TenantConfigurationPage() {

    const { logoUrl, refreshBranding } = useBranding();
    const [activeTab, setActiveTab] = useState(0);
    const [logoPreview, setLogoPreview] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadModalOpen, setUploadModalOpen] = useState(false);

    const handleUploadSuccess = async () => {
        setLogoPreview(null);
        await refreshBranding();
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                bgcolor: "#f8fafc",
            }}
        >
            <CustomHeader title="Configurations" />

            {/* Page Content */}
            <Box
                sx={{
                    px: { sm: 1, md: 2 },
                }}
            >
                {/* Tabs */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 1,
                        borderRadius: "20px",
                        border: "1px solid #e2e8f0",
                        display: "inline-flex",
                        mb: 1,
                    }}
                >
                    <Tabs
                        value={activeTab}
                        onChange={(e, value) =>
                            setActiveTab(value)
                        }
                        TabIndicatorProps={{
                            style: {
                                display: "none",
                            },
                        }}
                    >
                        {CONFIG_TABS.map(
                            (tab, index) => (
                                <Tab
                                    key={tab.key}
                                    disabled={
                                        tab.disabled
                                    }
                                    icon={tab.icon}
                                    iconPosition="start"
                                    label={tab.label}
                                    disableRipple
                                    sx={{
                                        textTransform:
                                            "none",
                                        minHeight:
                                            "unset",
                                        borderRadius:
                                            "14px",
                                        px: 3,
                                        py: 1.4,
                                        fontWeight: 700,
                                        fontSize:
                                            "0.95rem",
                                        transition:
                                            "all 0.2s ease",
                                    }}
                                />
                            )
                        )}
                    </Tabs>
                </Paper>

                {/* Branding Section */}
                {activeTab === 0 && (
                    <Stack spacing={4}>
                        <SectionCard
                            icon={
                                <Building2
                                    size={24}
                                />
                            }
                            title="Brand Assets"
                            subtitle="Configure organizational visual assets used throughout the platform including interview experiences, reports, email communication and recruiter workflows."
                        >
                            <Box
                                sx={{
                                    mt: 1,
                                }}
                            >
                                <LogoPreviewCard
                                    logoUrl={logoUrl}
                                    logoPreview={logoPreview}
                                    uploading={uploading}
                                    onUploadClick={() =>
                                        setUploadModalOpen(true)
                                    }
                                />
                            </Box>
                        </SectionCard>

                        {/* Future Modules */}

                        {/* <SectionCard
                            icon={<Settings2 size={24} />}
                            title="Upcoming Configuration Modules"
                            subtitle="This page is designed to evolve into a centralized tenant configuration system."
                        >
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: {
                                        xs: "1fr",
                                        md: "repeat(3,1fr)",
                                    },
                                    gap: 3,
                                }}
                            >
                                <FutureModuleCard
                                    title="Email Configuration"
                                    description="SMTP credentials, sender identities, branded templates and notification controls."
                                    icon={<Mail size={22} />}
                                />

                                <FutureModuleCard
                                    title="Domain & SSO"
                                    description="Custom domains, SAML, OAuth and organization-level authentication."
                                    icon={
                                        <ShieldCheck size={22} />
                                    }
                                />

                                <FutureModuleCard
                                    title="Theme & Experience"
                                    description="Custom colors, recruiter workspace themes and candidate-facing UI personalization."
                                    icon={<Palette size={22} />}
                                />
                            </Box>
                        </SectionCard> */}
                    </Stack>
                )}
            </Box>

            {/* Upload Modal */}
            <FileUploadModal
                open={uploadModalOpen}
                onClose={() =>
                    setUploadModalOpen(false)
                }
                uploadType="logo"
                setLoading={setUploading}
                apiEndpoint={clientApi.uploadLogo}
                onSuccess={handleUploadSuccess}
                onError={(err) => {
                    console.error(err);
                }}
            />
        </Box>
    );
}